import { BISHOP, BLACK, Chess, KING, KNIGHT, PAWN, ROOK, WHITE, } from "@chesshotel/chess";
import { exchangeEvaluation, getFen, key2pos, key2rank, pos2key, uci2move, } from "./util.js";
import { ChessTermType, TermAction, } from "@chesshotel/types";
import { isValidFenMove } from "./tactics/util.js";
import { getBackwardPawns, getIsolatedPawns } from "./pawnPlacement.js";
function protectedByPawn(chess, square, color) {
    const pos = key2pos(square);
    const behind = color == WHITE ? -1 : 1;
    let leftPawn = null;
    let rightPawn = null;
    if (pos[0] > 1) {
        const posLeftBehind = [pos[0] - 1, pos[1] + behind];
        const keyLeftBehind = pos2key(posLeftBehind);
        const leftBehindPiece = chess.getBoardPiece(keyLeftBehind);
        if (leftBehindPiece &&
            leftBehindPiece.color === color &&
            leftBehindPiece.type === PAWN) {
            leftPawn = leftBehindPiece;
        }
        if (pos[0] < 8) {
            const posRightBehind = [pos[0] + 1, pos[1] + behind];
            const keyRightBehind = pos2key(posRightBehind);
            const rightBehindPiece = chess.getBoardPiece(keyRightBehind);
            if (rightBehindPiece &&
                rightBehindPiece.color === color &&
                rightBehindPiece.type === PAWN) {
                rightPawn = rightBehindPiece;
            }
        }
    }
    const pawns = leftPawn && rightPawn ? [leftPawn, rightPawn] : leftPawn || rightPawn;
    return pawns;
}
function canBeKicked(chess, square, color) {
    const pos = key2pos(square);
    const columns = chess.boardPieceColumns();
    let leftPawn = false;
    let rightPawn = false;
    if (pos[0] > 1) {
        const leftColumn = columns[pos[0] - 2];
        const leftInfront = leftColumn.filter((piece) => {
            if (piece) {
                const piecePos = key2pos(piece.square);
                return color == WHITE ? piecePos[1] > pos[1] : piecePos[1] < pos[1];
            }
        });
        leftPawn = !!leftInfront.find((piece) => piece.type == PAWN);
    }
    if (pos[0] < 8) {
        const rightColumn = columns[pos[0]];
        const rightInfront = rightColumn.filter((piece) => {
            if (piece) {
                const piecePos = key2pos(piece.square);
                return color == WHITE ? piecePos[1] > pos[1] : piecePos[1] < pos[1];
            }
        });
        rightPawn = !!rightInfront.find((piece) => piece.type == PAWN);
    }
    return leftPawn || rightPawn;
}
function detectOutpost(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const move = chess.move(moveStr);
    if (move.piece != ROOK && move.piece != BISHOP && move.piece != KNIGHT) {
        return;
    }
    const protectingPawns = protectedByPawn(chess, move.to, color);
    const beKicked = canBeKicked(chess, move.to, color);
    const evaluation = exchangeEvaluation(chess.fen(), move.to);
    const traded = evaluation.gain >= 0 && evaluation.moveSequence.length > 0;
    if (protectingPawns && !beKicked && !traded) {
        const mainPiece = chess.getBoardPiece(move.to);
        const translationKeys = { piece: mainPiece.type, color };
        const arrows = Array.isArray(protectingPawns)
            ? [
                { from: protectingPawns[0].square, to: mainPiece.square },
                { from: protectingPawns[1].square, to: mainPiece.square },
            ]
            : [{ from: protectingPawns.square, to: mainPiece.square }];
        return {
            term: ChessTermType.OUTPOST,
            mainPiece,
            move,
            translationKeys,
            arrows,
        };
    }
}
function detectConnectRooks(fen, move) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const piecesBefore = chess.pieces(color);
    const rooks = piecesBefore.filter((piece) => piece.type == ROOK);
    if (rooks.length != 2) {
        return null;
    }
    const rooksConnected = chess.canDefend(rooks[0].square, rooks[1].square, color, true);
    if (rooksConnected) {
        return null;
    }
    const moveResult = chess.move(uci2move(move));
    const piecesAfter = chess.pieces(color);
    const rooksAfter = piecesAfter.filter((piece) => piece.type == ROOK);
    const rooksConnectedAfter = chess.canDefend(rooksAfter[0].square, rooksAfter[1].square, color, true);
    const arrows = [
        { from: rooksAfter[0].square, to: rooksAfter[1].square },
        { from: rooksAfter[1].square, to: rooksAfter[0].square },
    ];
    if (rooksConnectedAfter) {
        let term = ChessTermType.CONNECT_ROOKS;
        const rank1 = key2rank(rooksAfter[0].square);
        const rank2 = key2rank(rooksAfter[1].square);
        const sameRank = rank1 === rank2;
        if (sameRank) {
            const backRank = (rank1 == 1 && color == WHITE) || (rank1 == 8 && color == BLACK);
            if (backRank) {
                term = ChessTermType.CONNECT_ROOKS_BACK_RANK;
            }
        }
        return {
            term,
            mainPiece: chess.getBoardPiece(moveResult.to),
            move: moveResult,
            arrows,
        };
    }
}
export function detectPositivePlacement(position, lastPosition) {
    const outpost = detectOutpost(lastPosition.fen, position.move);
    if (outpost) {
        return { ...outpost, termAction: TermAction.PERFORM };
    }
    const connectRooks = detectConnectRooks(lastPosition.fen, position.move);
    if (connectRooks) {
        return { ...connectRooks, termAction: TermAction.PERFORM };
    }
    const blockade = detectBlockade(lastPosition.fen, position.move);
    if (blockade) {
        return { ...blockade, termAction: TermAction.PERFORM };
    }
    const activity = detectPieceActivity(lastPosition.fen, position.move);
    if (activity) {
        return { ...activity, termAction: TermAction.PERFORM };
    }
}
function addArrow(chessTerm) {
    if (chessTerm.arrows) {
        const arrow = { from: chessTerm.move.from, to: chessTerm.move.to };
        chessTerm.arrows.push(arrow);
    }
}
function detectNegativeOutpost(position, lastPosition) {
    const user = detectOutpost(lastPosition.fen, position.move);
    const best = detectOutpost(lastPosition.fen, lastPosition.bestMove);
    if (!user && best) {
        addArrow(best);
        return { ...best, termAction: TermAction.MISS };
    }
    if (user && best) {
        addArrow(best);
        return { ...best, termAction: TermAction.BETTER_WAY };
    }
    const opponentMove = position.pvMoves[0];
    const userAllow = detectOutpost(position.fen, opponentMove);
    if (userAllow) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        if (!isValid) {
            addArrow(userAllow);
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
        const bestAllow = isValid && detectOutpost(bestFen, opponentMove);
        if (!bestAllow) {
            addArrow(userAllow);
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
    }
}
function detectNegativePieceActivity(position, lastPosition) {
    const user = detectPieceActivity(lastPosition.fen, position.move);
    const best = detectPieceActivity(lastPosition.fen, lastPosition.bestMove);
    if (!user && best) {
        return { ...best, termAction: TermAction.MISS };
    }
    if (user && best && user.mainPiece.type === best.mainPiece.type) {
        return { ...best, termAction: TermAction.BETTER_WAY };
    }
    const opponentMove = position.pvMoves[0];
    const userAllow = detectPieceActivity(position.fen, opponentMove);
    if (userAllow) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        if (!isValid) {
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
        const bestAllow = isValid && detectPieceActivity(bestFen, opponentMove);
        if (!bestAllow) {
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
    }
}
function detectNegativeBlockade(position, lastPosition) {
    const user = detectBlockade(lastPosition.fen, position.move);
    const best = detectBlockade(lastPosition.fen, lastPosition.bestMove);
    if (!user && best) {
        return { ...best, termAction: TermAction.MISS };
    }
    if (user && best) {
        return { ...best, termAction: TermAction.BETTER_WAY };
    }
    const opponentMove = position.pvMoves[0];
    const userAllow = detectBlockade(position.fen, opponentMove);
    if (userAllow) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        if (!isValid) {
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
        const bestAllow = isValid && detectBlockade(bestFen, opponentMove);
        if (!bestAllow) {
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
    }
}
function detectNegativeConnectRooks(position, lastPosition) {
    const user = detectConnectRooks(lastPosition.fen, position.move);
    const best = detectConnectRooks(lastPosition.fen, lastPosition.bestMove);
    if (!user && best) {
        return { ...best, termAction: TermAction.MISS };
    }
    if (user && best) {
        return {
            ...best,
            arrows: [best.move],
            termAction: TermAction.BETTER_WAY,
        };
    }
    const opponentMove = position.pvMoves[0];
    const userAllow = detectConnectRooks(position.fen, opponentMove);
    if (userAllow) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        if (!isValid) {
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
        const bestAllow = isValid && detectConnectRooks(bestFen, opponentMove);
        if (!bestAllow) {
            return { ...userAllow, termAction: TermAction.ALLOW };
        }
    }
    return null;
}
export function detectNegativePlacement(position, lastPosition) {
    const outpost = detectNegativeOutpost(position, lastPosition);
    if (outpost)
        return outpost;
    const connectRooks = detectNegativeConnectRooks(position, lastPosition);
    if (connectRooks)
        return connectRooks;
    const blockade = detectNegativeBlockade(position, lastPosition);
    if (blockade)
        return blockade;
    const pieceActivity = detectNegativePieceActivity(position, lastPosition);
    if (pieceActivity)
        return pieceActivity;
}
function detectPieceActivity(fen, moveStr) {
    const chess = new Chess(fen);
    const moveObj = uci2move(moveStr);
    const piece = chess.get(moveObj.from);
    if (piece && (piece.type == KING || piece.type == PAWN))
        return null;
    const movesBefore = chess.moves({
        square: moveObj.from,
        verbose: true,
    });
    const move = chess.move(moveObj);
    chess.changeTurn();
    const movesAfter = chess.moves({
        square: moveObj.to,
        verbose: true,
    });
    const threatsBefore = movesBefore.filter((m) => m.captured);
    const threatsAfter = movesAfter.filter((m) => m.captured);
    const increasedOrEqualThreats = threatsAfter.length >= threatsBefore.length;
    const increasedMoves = movesAfter.length > movesBefore.length;
    if (increasedMoves && increasedOrEqualThreats) {
        const mainPiece = chess.getBoardPiece(moveObj.to);
        const translationKeys = { piece: mainPiece.type };
        return {
            term: ChessTermType.PIECE_ACTIVITY,
            move,
            mainPiece,
            translationKeys,
        };
    }
}
function detectBlockade(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const moveObj = uci2move(moveStr);
    const posTo = key2pos(moveObj.to);
    const posInfront = color === WHITE ? [posTo[0], posTo[1] + 1] : [posTo[0], posTo[1] - 1];
    const squareInfront = pos2key(posInfront);
    const pieceInfront = chess.get(squareInfront);
    if (!pieceInfront || pieceInfront.type !== PAWN) {
        return null;
    }
    const move = chess.move(uci2move(moveStr));
    const opponentColor = chess.turn();
    const backwardPawns = getBackwardPawns(chess.fen(), opponentColor);
    const isolatedPawns = getIsolatedPawns(chess.fen(), opponentColor);
    const backwardPawn = backwardPawns.find(({ square }) => square === squareInfront);
    const isolatedPawn = isolatedPawns.find(({ square }) => square === squareInfront);
    if (!backwardPawn && !isolatedPawn) {
        return null;
    }
    const mainPiece = chess.getBoardPiece(moveObj.to);
    const square = backwardPawn?.square || isolatedPawn?.square;
    const translationKeys = { piece: mainPiece.type, square };
    return {
        term: ChessTermType.BLOCKADE,
        move,
        mainPiece,
        translationKeys,
    };
}
//# sourceMappingURL=placement.js.map